import axios from "axios";
import { getAccessToken, refreshAccessToken, isBlockedUserError } from "../utils/auth0Utils";
import { BLOCKED_USER_FLAG } from "../utils/constants";
import { PARTNER_CONFIG } from "../config/partnerConfig";

const api = axios.create({
  baseURL: process.env.REACT_APP_API_URL || "http://localhost:8080",
  headers: { "Content-Type": "application/json" },
});

/**
 * Track whether a refresh call is currently running so we do not send
 * multiple refresh requests in parallel.
 */
let isRefreshing = false;

/**
 * Queue of pending requests that failed with 401/403 while a refresh is in progress.
 * Each entry holds the resolve/reject callbacks created when we wrap the retry
 * logic in a Promise.
 */
let refreshQueue = [];

/**
 * Add a request to the refresh queue. The promise resolves once a new access
 * token is available (or rejects if refresh fails).
 */
const enqueueRequest = () =>
  new Promise((resolve, reject) => {
    refreshQueue.push({ resolve, reject });
  });

/**
 * Resolve or reject all queued requests with the provided token/error.
 */
const resolveQueue = (error, token = null) => {
  refreshQueue.forEach(({ resolve, reject }) => {
    if (error) reject(error);
    else resolve(token);
  });
  refreshQueue = [];
};

/**
 * Helper to attach the Authorization header to an axios config object.
 */
const attachToken = (config, token) => {
  if (token) {
    config.headers = config.headers || {};
    config.headers.Authorization = `Bearer ${token}`;
  }
};

/**
 * Decide whether the response should trigger a refresh attempt.
 */
const shouldRetry = (status, request) =>
  Boolean(request) && !request._retry && (status === 401 || status === 403);

const PARTNER_ROUTE_KEYS = new Set(
  Object.keys(PARTNER_CONFIG || {}).map((key) => key.toLowerCase())
);

/**
 * Check if the current route is a partner submission route.
 */
const isPartnerSubmissionRoute = () => {
  if (typeof window === "undefined" || !window.location?.pathname) return false;

  const cleanPath = window.location.pathname.replace(/^\/|\/$/g, "");
  if (!cleanPath) return true;

  const firstSegment = cleanPath.split("/")[0].toLowerCase();
  return PARTNER_ROUTE_KEYS.has(firstSegment);
};

/**
 * Check if the current route is a public route that doesn't require authentication.
 */
const isPublicRoute = () => {
  if (typeof window === "undefined" || !window.location?.pathname) return false;
  
  const pathname = window.location.pathname;
  
  // Rate review public routes (form and results)
  if (pathname.startsWith("/rate-review")) {
    return true;
  }
  
  // Partner submission routes
  if (isPartnerSubmissionRoute()) {
    return true;
  }
  
  return false;
};

/**
 * Check if this is a public API request that doesn't require authentication
 */
const isPublicApiRequest = (url) => {
  if (!url) return false;
  
  // Rate review public endpoints
  if (url.includes("/api/rate-review/public/") || url.includes("api/rate-review/public/")) {
    return true;
  }
  
  // Rate review form submission (POST to /api/rate-review without /list or /public/)
  if (url.includes("/api/rate-review") && !url.includes("/list") && !url.includes("/public/")) {
    return true;
  }
  
  return false;
};

/**
 * Always ensure outgoing requests include the most recent access token,
 * except on public routes (partner submission, rate-review) where requests are public.
 */
api.interceptors.request.use(
  async (request) => {
    // Check if this is a public API request (rate-review public endpoints)
    const isPublicApiRequest = request.url && (
      request.url.includes("/api/rate-review/public/") ||
      request.url.includes("api/rate-review/public/")
    );
    
    // Don't add auth token for public routes or public API requests
    if (!isPublicRoute() && !isPublicApiRequest) {
      const token = await getAccessToken();
      attachToken(request, token);
    }
    return request;
  },
  (error) => Promise.reject(error)
);

api.interceptors.response.use(
  (response) => response,
  async (error) => {
    const { response: errorResponse, config: originalRequest } = error || {};
    const status = errorResponse?.status;
    const errorData = errorResponse?.data;

    // Check if the error indicates a blocked user
    // This can happen if:
    // 1. The error response contains blocked user information
    // 2. The error message contains blocked user indicators
    // 3. The status is 403 with a specific blocked user message
    const isBlockedError = 
      status === 403 && (
        errorData?.message?.toLowerCase().includes('blocked') ||
        errorData?.error?.toLowerCase().includes('blocked') ||
        errorData?.blocked === true ||
        (typeof errorData === 'string' && errorData.toLowerCase().includes('blocked'))
      );

    // Check if the error object itself indicates a blocked user
    const isBlockedAuth0Error = error && isBlockedUserError(error);

    if (isBlockedError || isBlockedAuth0Error) {
      // Store flag to show error message
      localStorage.setItem(BLOCKED_USER_FLAG, 'true');
      // Clear Auth0 storage to force re-login
      // Note: We don't need to import clearAuth0Storage here since we're just setting a flag
      // The error will be handled by the UI layer
    }

    // Check if this is a public API request - don't retry auth for these
    const isPublicApi = isPublicApiRequest(originalRequest?.url);
    
    // Don't retry auth refresh for public API requests
    if (shouldRetry(status, originalRequest) && !isPublicApi) {
      if (isRefreshing) {
        const token = await enqueueRequest();
        attachToken(originalRequest, token);
        return api(originalRequest);
      }

      originalRequest._retry = true;
      isRefreshing = true;

      try {
        const newToken = await refreshAccessToken();
        resolveQueue(null, newToken);
        attachToken(originalRequest, newToken);
        return api(originalRequest);
      } catch (refreshError) {
        // Check if refresh error is due to blocked user
        if (isBlockedUserError(refreshError)) {
          localStorage.setItem(BLOCKED_USER_FLAG, 'true');
        }
        resolveQueue(refreshError);
        throw refreshError;
      } finally {
        isRefreshing = false;
      }
    }

    return Promise.reject(error);
  }
);

export default api;
